import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Search, Loader2, Truck, X } from "lucide-react";
import { vehiculoService, type Vehiculo } from '../../services/maintenance/vehiculoService';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import toast from 'react-hot-toast';

interface VehicleAssignmentModalProps {
    open: boolean;
    onClose: () => void;
    planId: number;
    planNombre: string;
}

export const VehicleAssignmentModal: React.FC<VehicleAssignmentModalProps> = ({ open, onClose, planId, planNombre }) => {
    const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
    const [loading, setLoading] = useState(false);
    const [actioning, setActioning] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const loadData = async () => {
        setLoading(true);
        try {
            const vRes = await vehiculoService.getAll();
            setVehiculos(vRes.data || vRes);
            // Ya no necesitamos getAssignedVehicles por separado porque vienen en vRes
        } catch (error) {
            console.error("Error loading vehicles:", error);
            toast.error("Error al cargar vehículos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open, planId]);

    const handleAssign = async (vehiculoId: string) => {
        setActioning(vehiculoId);
        try {
            await maintenanceService.assignVehicle(planId, vehiculoId);
            toast.success("Vehículo vinculado correctamente");
            loadData();
        } catch (error) {
            console.error("Error assigning vehicle:", error);
            toast.error("Error al vincular vehículo");
        } finally {
            setActioning(null);
        }
    };

    const handleUnassign = async (vehiculoId: string) => {
        setActioning(vehiculoId);
        try {
            await maintenanceService.unassignVehicle(planId, vehiculoId);
            toast.success("Vehículo desvinculado");
            loadData();
        } catch (error) {
            console.error("Error unassigning vehicle:", error);
            toast.error("Error al desvincular vehículo");
        } finally {
            setActioning(null);
        }
    };

    const getVehicleStatus = (v: any) => {
        const planAsignado = v.plan_asignado;
        if (!planAsignado) return 'none';
        if (planAsignado.plan_maestro_id === planId) return 'current';
        return 'other';
    };

    const filteredVehiculos = (Array.isArray(vehiculos) ? vehiculos : []).filter(v =>
        v.placa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.modelo && v.modelo.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-950 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-t-lg">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                            <Truck className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">
                                Vincular Vehículos al Plan
                            </h2>
                            <p className="text-xs text-muted-foreground">
                                Plan: <span className="font-semibold">{planNombre}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Filter */}
                <div className="px-6 py-4 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                        <Input
                            placeholder="Buscar por placa o modelo..."
                            className="pl-9 h-10 bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <Loader2 className="h-10 w-10 animate-spin mb-4 text-blue-500" />
                            <span className="text-sm font-medium">Cargando flota de vehículos...</span>
                        </div>
                    ) : filteredVehiculos.length === 0 ? (
                        <div className="text-center py-20">
                            <Truck className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No se encontraron vehículos disponibles.</p>
                            <p className="text-xs text-gray-400 mt-1">Intente con otro término de búsqueda</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {filteredVehiculos
                                .map((v: any) => {
                                    const status = getVehicleStatus(v);
                                    const otherPlanName = v.plan_asignado?.plan_maestro?.nombre;

                                    return (
                                        <div key={v.id} className={`flex items-center justify-between p-3 border rounded-lg transition-all ${status === 'current' ? 'bg-blue-50/50 border-blue-100' :
                                            status === 'other' ? 'bg-amber-50/30 border-amber-100' :
                                                'hover:bg-slate-50 border-slate-100'
                                            }`}>
                                            <div className="flex items-center gap-4">
                                                <div className={`h-11 w-11 rounded-xl shadow-sm flex items-center justify-center transition-colors ${status === 'current' ? "bg-blue-600 text-white" :
                                                    status === 'other' ? "bg-amber-500 text-white" :
                                                        "bg-slate-100 text-slate-400"
                                                    }`}>
                                                    <Truck className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-black text-slate-900 tracking-tight">{v.placa}</span>
                                                        {status === 'other' && (
                                                            <span className="text-[10px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-widest border border-amber-200">
                                                                En otro plan
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[11px] font-bold text-slate-400 truncate uppercase">
                                                            {v.marca} • {v.modelo}
                                                        </span>
                                                        {status === 'other' && (
                                                            <span className="text-[10px] font-bold text-amber-600 truncate mt-0.5 italic">
                                                                Plan: {otherPlanName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {status === 'current' ? (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-9 px-4 font-black text-[11px] text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                                                        onClick={() => handleUnassign(v.id)}
                                                        disabled={actioning === v.id}
                                                    >
                                                        {actioning === v.id ? <Loader2 size={14} className="animate-spin" /> : "DESVINCULAR"}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        className={`h-9 px-4 font-black text-[11px] rounded-xl transition-all ${status === 'other'
                                                            ? "bg-amber-500 hover:bg-amber-600 text-white"
                                                            : "bg-blue-600 hover:bg-blue-700 text-white"
                                                            }`}
                                                        onClick={() => handleAssign(v.id)}
                                                        disabled={actioning === v.id}
                                                    >
                                                        {actioning === v.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            status === 'other' ? "TRANSFERIR" : "VINCULAR"
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end bg-slate-50/50 dark:bg-slate-900/50 rounded-b-lg">
                    <Button variant="outline" onClick={onClose} className="px-6">
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
};
